import React, { useRef, useEffect, useMemo, useState, useCallback } from "react";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { MDSSession } from "@/mds/components/sessions/session";
import {
  IMDSSessionProps,
  IMDSSessionRef,
  SessionEvent,
  SessionFeature,
  SessionStatus,
} from "@/mds/components/sessions/session.types";
import { useAppStore } from "@/store";
import { MdsDropdownIconButton } from "@/design-system/components/dropdown";
import { MdsIconKind } from "@/design-system/components/icon";
import { css } from "@/domains/emotion";
import { AnimationTiming } from "@/design-system/constants/common";
import { MemCommonMentionKind } from "@mem-labs/common-editor";
import { SessionObservable } from "@/store/sessions/SessionObservable";
import {
  MDSButtonGroup,
  MDSComboboxVariant,
  MDSIconButton,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSPanelHeader,
  MDSPanelHeaderSlotName,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
  MDSPanelHeaderRounded,
  useTheme,
  MDSTextColor,
} from "@/mds";
import { FloatingNotePreview } from "@/design-system/components/item-list/rows/item-preview/FloatingNotePreview";
import { HoveringState } from "@/design-system/components/item-list/rows/item-preview/HoveringState";
import { Maybe } from "@/domains/common/types";
import { INoteObservable } from "@/store/note";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { SessionEventModelData } from "@/store/sessions/types";
import { MemItFeedbackModal } from "./feedback/MemItFeedbackModal";
import EmptyOverlay from "./EmptyOverlay";
import { MDSComboboxMention } from "@/mds/components/forms/combobox/combobox.types";

interface MemItSidePanelViewProps {
  sessionId: string;
  inSidePanel?: boolean;
  isClosed?: boolean;
  onClose?: () => void;
}

export const MemItActions = observer(() => {
  const { pageStore } = useAppStore();
  const memItStore = pageStore.memItPage;

  const hasPreviousSessions = useMemo(() => {
    return memItStore.previousChatSessions.length > 0;
  }, [memItStore.previousChatSessions]);

  const contentList = useMemo(() => {
    return memItStore.previousSessionsContentList;
  }, [memItStore.previousSessionsContentList]);

  const newSessionLabel = "New Chat";
  const previousSessionsLabel = "Past Chats";

  return (
    <>
      <MDSTooltip
        config={{
          label: newSessionLabel,
          placement: MDSTooltipPlacement.Top,
          delaySeconds: AnimationTiming.TooltipHoverDelaySeconds,
        }}
      >
        <MDSIconButton iconKind={MdsIconKind.Plus} onClick={memItStore.handleNewSession} />
      </MDSTooltip>
      {hasPreviousSessions && (
        <MdsDropdownIconButton
          iconKind={MdsIconKind.History}
          contentList={contentList}
          placement="below-right-alignment"
          innerStyles={{
            Content: {
              className: contentStyles,
            },
          }}
          tooltipConfig={{
            label: previousSessionsLabel,
            placement: MDSTooltipPlacement.Top,
            delaySeconds: AnimationTiming.TooltipHoverDelaySeconds,
          }}
        />
      )}
    </>
  );
});

export const MemItSidePanelView = observer<MemItSidePanelViewProps>(
  ({ sessionId, isClosed = false, onClose }) => {
    const { pageStore } = useAppStore();
    const memItStore = pageStore.memItPage;

    useEffect(() => {
      // Explicitly initialize route mentions on mount (MobX reaction handles subsequent changes)
      memItStore.initializeCurrentObservableFromRoute();
    }, [memItStore]);

    useEffect(() => {
      // Sync the store's active session after render commit to avoid setState during render
      memItStore.setSessionId(sessionId);
    }, [sessionId, memItStore]);

    // If session is not available yet, show the UI structure immediately
    // but with empty session props - this eliminates the loading screen
    if (!memItStore.session || !memItStore.session.id) {
      return null;
    }

    return (
      <Inner
        sessionObservable={memItStore.session}
        isClosed={isClosed}
        onClose={onClose}
        mentions={memItStore.currentMentions}
      />
    );
  }
);

const Inner = observer(
  ({
    sessionObservable,
    isClosed = false,
    mentions = [],
  }: {
    sessionObservable: SessionObservable;
    isClosed?: boolean;
    onClose?: () => void;
    mentions?: MDSComboboxMention[];
  }) => {
    const { store, pageStore } = useAppStore();
    const [hoverState] = useState(new HoveringState());
    const [hoveredNote, setHoveredNote] = useState<Maybe<INoteObservable>>();
    const [hoveredText, setHoveredText] = useState<string | undefined>(undefined);
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const [isFeedbackPositive, setIsFeedbackPositive] = useState(false);
    const [currentFeedbackEventId, setCurrentFeedbackEventId] = useState<string | undefined>();
    const { theme } = useTheme();
    const onLeftAddonButtonClick = useCallback(() => {
      if (!isClosed) return;
      store.sidePanel.goToMemIt(sessionObservable.id);
    }, [isClosed, store, sessionObservable.id]);

    const memItStore = pageStore.memItPage;

    const handleFeedback = useCallback(
      async (eventId: string, responseKind: "UPVOTE" | "DOWNVOTE") => {
        try {
          // Submit feedback immediately when user clicks
          memItStore.provideFeedback(eventId, responseKind);

          // Then show modal to collect additional details
          setCurrentFeedbackEventId(eventId);
          setIsFeedbackPositive(responseKind === "UPVOTE");
          setIsFeedbackModalOpen(true);
        } catch (error) {
          logger.error({
            message: "Failed to provide initial MemIt feedback",
            info: { error: objectModule.safeErrorAsJson(error as Error) },
          });
        }
      },
      [memItStore]
    );

    const handleCloseFeedbackModal = useCallback(() => {
      setIsFeedbackModalOpen(false);
      setCurrentFeedbackEventId(undefined);
    }, []);

    const handleSubmitFeedback = useCallback(
      async (suggestedFeedback: string | undefined, userText: string) => {
        if (!currentFeedbackEventId) return;

        try {
          const responseKind = isFeedbackPositive ? "UPVOTE" : "DOWNVOTE";
          const selectedSuggestions = suggestedFeedback ? [suggestedFeedback] : undefined;

          // Submit additional feedback details (this updates the existing feedback)
          await memItStore.provideFeedback(
            currentFeedbackEventId,
            responseKind,
            userText || undefined,
            selectedSuggestions
          );

          setIsFeedbackModalOpen(false);
          setCurrentFeedbackEventId(undefined);
        } catch (error) {
          logger.error({
            message: "Failed to provide additional MemIt feedback details",
            info: { error: objectModule.safeErrorAsJson(error as Error) },
          });
        }
      },
      [currentFeedbackEventId, isFeedbackPositive, memItStore]
    );

    const sessionRef = useRef<IMDSSessionRef>(null);
    const sessionData = sessionObservable.modelData;

    // Resume suggestion state - mirrors ChatView logic
    const [showEmptyOverlay, setShowEmptyOverlay] = useState(false);

    // Check if current session is empty (no events)
    const showEmptyState = memItStore.sessionEvents.length === 0 && !isClosed;

    // Check for recent sessions when a new session is created - mirrors ChatView logic
    useEffect(() => {
      if (!showEmptyState) {
        setShowEmptyOverlay(false);
        return;
      }

      const recent = memItStore.previousChatSessions[0];
      if (recent && recent.modelId !== sessionObservable.id) {
        setShowEmptyOverlay(memItStore.sessionEvents.length === 0);
        return;
      }

      if (!recent) {
        setShowEmptyOverlay(true);
        return;
      }

      setShowEmptyOverlay(false);
    }, [sessionObservable.id, showEmptyState, memItStore]);

    // Update all events when they change
    useEffect(() => {
      const sessionEvents = exportSessionEvents(memItStore.sessionEvents);
      sessionRef.current?.upsertEvents(sessionEvents);
    }, [memItStore.sessionEvents]);

    useEffect(() => {
      if (sessionData?.status) {
        sessionRef.current?.updateStatus(sessionData.status as SessionStatus);
      }
    }, [sessionData?.status]);

    const linkHandler = (
      kind: MemCommonMentionKind,
      id: string,
      target: HTMLElement | null,
      event: React.MouseEvent<Element>,
      meta?: { noteVersion?: number | null }
    ) => {
      event.preventDefault();
      store.navigation.goToMention({
        id: id,
        mentionKind: kind,
        url: `${kind}/${id}`,
        highlightText: target?.textContent ?? undefined,
        keys: event,
        historyVersion: meta?.noteVersion ? meta.noteVersion : undefined,
        suppressSidePanelRouteHandler: true,
      });
    };

    const handleMemItemOver = useCallback(
      (itemKind: MemCommonMentionKind, itemId: string, target: HTMLElement | null) => {
        if (itemKind !== MemCommonMentionKind.Note) {
          return;
        }

        if (!target) {
          return;
        }

        hoverState.setHoveredRect(target?.getBoundingClientRect());
        setHoveredText(target?.innerText);
        setHoveredNote(store.notes.getNoteObservableById({ noteId: itemId }));
        hoverState.handleMouseEnter();
      },
      [store.notes, hoverState]
    );

    const handleMemItemOut = useCallback(() => {
      hoverState.setHoveredRect(null);
      setHoveredNote(undefined);
      hoverState.handleMouseLeave();
    }, [hoverState]);

    const sessionProps: IMDSSessionProps = {
      sessionTitle: "Mem It",
      sessionSubtitle: `Session: ${sessionObservable.id}`,
      initialSessionEvents: [],
      initialSessionStatus: (sessionData?.status as SessionStatus) || SessionStatus.Initial,
      containerHeight: 400, // Smaller height for sidebar
      isOpen: !isClosed,
      config: {
        features: [SessionFeature.ContextLimitExceededNewChatButton],
        openLinksInNewTab: false,
        linkHandler: linkHandler,
        onMemItemOver: handleMemItemOver,
        onMemItemOut: handleMemItemOut,
        feedbackHandler: handleFeedback,
      },
      comboboxProps: {
        placeholder: "Tell or ask Mem something",
        variant: MDSComboboxVariant.Brand,
        chatIsClosed: isClosed,
        fetchIsInstant: true,
        mentions: mentions,
        implicitMentions: memItStore.currentImplicitMentions,
        onMentionAdd: memItStore.handleMentionAdd,
        onMentionRemove: memItStore.handleMentionRemove,
        onMentionsClear: memItStore.handleMentionsClear,
        onLeftAddonButtonClick: onLeftAddonButtonClick,
      },
      handlers: {
        handleFetchComboboxData: memItStore.handleComboboxFetch,
        handleInputFormSubmit: memItStore.handleCreateUserMessage,
        handleNewSession: memItStore.handleNewSession,
        handleViewModeChanged: () => {},
        handleCountdownCompleted: () => {},
        handleRetry: async () => {
          // Clear existing session error before retry attempt
          sessionRef.current?.clearSessionError();
          await store.sessions.processSession(sessionObservable.id);
        },
      },
    };

    return (
      <Container data-component="MemItSidePanelView" $isInteractive={isClosed}>
        <PanelHeaderContainer $isClosed={isClosed} $isInteractive={isClosed}>
          <MDSPanelHeader
            data-component="MDSPanelHeader"
            rounded={MDSPanelHeaderRounded.TopRight}
            backgroundColor={theme.colors.surface.chat}
            bottomBorderColor={theme.colors.border.onAccent}
            isSidePanel
          >
            <HeadingContent data-component="HeadingContent" slot={MDSPanelHeaderSlotName.Heading}>
              <MDSText
                variant={MDSTextVariant.TextS}
                weight={MDSTextWeight.Smedium}
                color={MDSTextColor.Contrast}
              >
                Chat
              </MDSText>
            </HeadingContent>
            {!isClosed && (
              <MDSButtonGroup
                slot={MDSPanelHeaderSlotName.Actions}
                data-test-id="mem-it-actions-group"
              >
                <MemItActions />
              </MDSButtonGroup>
            )}
          </MDSPanelHeader>
        </PanelHeaderContainer>
        {showEmptyOverlay && (
          <EmptyOverlay currentSessionId={sessionObservable.id} isClosed={isClosed} />
        )}
        <SessionContainer data-component="SessionContainer">
          <MDSSession ref={sessionRef} key={sessionObservable.id} {...sessionProps} />
        </SessionContainer>
        <MemItFeedbackModal
          isModalOpen={isFeedbackModalOpen}
          handleCloseModal={handleCloseFeedbackModal}
          isPositive={isFeedbackPositive}
          onSubmit={handleSubmitFeedback}
        />
        <FloatingNotePreview
          state={hoverState}
          noteObservable={hoveredNote}
          placement="right"
          highlightText={hoveredText}
        />
      </Container>
    );
  }
);

const Container = styled.div<{ $isInteractive: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  cursor: ${({ $isInteractive }) => ($isInteractive ? "pointer" : "default")};
  border-top-right-radius: ${({ theme }) => theme.borderRadius.xlarge};
`;

const SessionContainer = styled.div`
  flex: 1;
  overflow: auto;
  background-color: ${({ theme }) => theme.colors.surface.chat};
`;

const contentStyles = css({
  maxWidth: "320px",
  overflow: "auto",
  maxHeight: "min(calc(100vh - 140px), 600px)",
});

const exportSessionEvents = (sessionEvents: SessionEventModelData[]): SessionEvent[] => {
  return sessionEvents.map(event => ({
    id: event.id,
    session_id: event.session_id,
    locally_created_at: event.locally_created_at,
    internal_created_at: event.locally_created_at,
    internal_updated_at: event.locally_created_at,
    feedbackGiven: event.feedback_given,
    data: {
      kind: event.kind,
      value: event.value_json,
    } as SessionEvent["data"],
  }));
};

const HeadingContent = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  gap: ${({ theme }) => theme.spacing["0.5"]};
`;

const PanelHeaderContainer = styled.div<{ $isClosed: boolean; $isInteractive: boolean }>`
  position: relative;
  overflow: hidden;
  max-height: ${({ $isClosed }) => ($isClosed ? "0px" : "auto")};
  height: ${({ $isClosed }) => ($isClosed ? "0px" : "auto")};
  min-height: ${({ $isClosed }) => ($isClosed ? "0px" : "auto")};
  transition: height 0.16s ease;
`;
